import { Scene } from "three";
import { GLTFLoader, GLTF } from "three/examples/jsm/loaders/GLTFLoader.js";
import { GLTFExporter } from "three/examples/jsm/exporters/GLTFExporter.js";

//TODO: later add zip and unzip 3B folder ?? pro no bc not much size savings + longer load time

// Define a type for the data we'll store in userData.
// Storing files directly in JSON is not possible, so we convert them to Base64.
interface IStoredFileData {
    name: string;
    type: string;
    data: string; // Base64 Data URL
}

interface ICustomUserData {
    B: string;
    files: IStoredFileData[];
}

export class GlbManager {
    private gltf: GLTF | null = null;
    private readonly USER_DATA_KEY = "3B"; // A unique key to avoid conflicts

    /**
     * Imports and loads a GLB model from either a URL or a File object.
     * The loaded scene is stored internally in the class instance.
     * @param source The URL (string) or File object of the GLB model.
     * @returns A Promise that resolves when the model is loaded.
     */
    public async importGLB(source: string | File): Promise<void> {
        const loader = new GLTFLoader();

        return new Promise((resolve, reject) => {
            const onLoad = (gltf: GLTF) => {
                this.gltf = gltf;
                //console.log("GLB loaded successfully.", this.gltf);
                console.log(
                    "GLB loaded. Checking scene.userData directly: ",
                    this.gltf.scene.userData
                );
                resolve();
            };

            const onError = (error: unknown) => {
                console.error("An error happened during GLB loading:", error);
                reject(error);
            };

            if (typeof source === "string") {
                // Loading from a URL
                loader.load(source, onLoad, undefined, onError);
            } else {
                // Loading from a File object
                const reader = new FileReader();
                reader.onload = (event) => {
                    const arrayBuffer = event.target?.result as ArrayBuffer;
                    if (arrayBuffer) {
                        loader.parse(arrayBuffer, "", onLoad, onError);
                    } else {
                        reject(
                            new Error("Failed to read file as ArrayBuffer.")
                        );
                    }
                };
                reader.onerror = (error) => reject(error);
                reader.readAsArrayBuffer(source);
            }
        });
    }

    // grep -a '"extras"' ./netflix.3B.glb
    public async addDataAndExport(
        data: string,
        files: File[] = []
    ): Promise<Blob> {
        if (!this.gltf || !this.gltf.scene) {
            throw new Error(
                "No GLB model loaded. Please import a model first."
            );
        }

        // 1. Prepare the data to be embedded.
        const fileDataPromises = files.map((file) => this.fileToBase64(file));
        const storedFiles: IStoredFileData[] = await Promise.all(
            fileDataPromises
        );
        const userData: ICustomUserData = {
            B: data,
            files: storedFiles
        };

        // 2. --- THE "CLEAN SLATE" STRATEGY ---
        // Create a brand new, clean scene object. This scene has no connection
        // to the GLTFLoader's internal parser or cache.
        const cleanScene = new Scene();

        // 3. Attach our custom data to the USERDATA of this NEW scene.
        cleanScene.userData[this.USER_DATA_KEY] = userData;
        console.log(
            "Attached data to a new, clean scene.",
            cleanScene.userData
        );

        // 4. Move all children from the loaded scene into our clean scene.
        // When an object is added to a new parent, Three.js automatically
        // removes it from the old one. A while loop is the safest way.
        while (this.gltf.scene.children.length > 0) {
            cleanScene.add(this.gltf.scene.children[0]);
        }
        console.log("Moved all 3D objects to the clean scene.");

        // 5. Export the CLEAN scene.
        // The exporter will now be forced to serialize the scene graph from
        // scratch, without relying on any potentially corrupted cached data.
        const exporter = new GLTFExporter();
        return new Promise((resolve, reject) => {
            exporter.parse(
                cleanScene, // We pass our single clean scene
                (result) => {
                    if (result instanceof ArrayBuffer) {
                        const blob = new Blob([result], {
                            type: "model/gltf-binary"
                        });
                        resolve(blob);
                    } else {
                        reject(
                            new Error("Exporter did not return an ArrayBuffer.")
                        );
                    }
                },
                (error) => {
                    console.error(
                        "An error occurred during GLB export:",
                        error
                    );
                    reject(error);
                },
                {
                    binary: true,
                    // IMPORTANT: Animations are not part of the scene graph,
                    // so we must pass them in from the original GLTF object.
                    animations: this.gltf!.animations
                }
            );
        });
    }

    /**
     * Inserts a string and an array of files into the loaded GLB's scene userData
     * and exports the modified model as a GLB Blob.
     * @param customString A string to store in the user data.
     * @param files An array of File objects to embed.
     * @returns A Promise that resolves with a Blob of the new GLB file.
     */
    public async addDataAndExport2(
        data: string,
        files: File[] = []
    ): Promise<Blob> {
        if (!this.gltf || !this.gltf.scene) {
            throw new Error(
                "No GLB model loaded. Please import a model first."
            );
        }

        // 1. Convert all files to Base64 strings to make them JSON-serializable.
        const fileDataPromises = files.map((file) => this.fileToBase64(file));
        const storedFiles: IStoredFileData[] = await Promise.all(
            fileDataPromises
        );

        // 2. Create the data structure to be stored.
        const userData: ICustomUserData = {
            B: data,
            files: storedFiles
        };

        // 3. Attach the data to the scene's userData property under a unique key.
        this.gltf.scene.userData[this.USER_DATA_KEY] = userData;
        console.log("Attaching user data to the scene:", userData);

        // 4. Export the modified scene as a GLB.
        const exporter = new GLTFExporter();
        return new Promise((resolve, reject) => {
            exporter.parse(
                this.gltf!.scenes, // Pass the array of all scenes
                (result) => {
                    if (result instanceof ArrayBuffer) {
                        const blob = new Blob([result], {
                            type: "model/gltf-binary"
                        });
                        resolve(blob);
                    } else {
                        // Should not happen with binary: true, but handle it
                        const jsonString = JSON.stringify(result);
                        const blob = new Blob([jsonString], {
                            type: "application/json"
                        });
                        resolve(blob);
                    }
                },
                (error) => {
                    console.error(
                        "An error occurred during GLB export:",
                        error
                    );
                    reject(error);
                },
                {
                    binary: true, // Crucial option for exporting as a single GLB file
                    animations: this.gltf!.animations // Also include animations
                }
            );
        });
    }

    /**
     * Reads the custom user data from the currently loaded GLB model.
     * @returns A Promise that resolves with the string and an array of File objects, or null if no data is found.
     */
    public async readUserData(): Promise<{
        data: string;
        diamonds: File[];
    }> {
        if (!this.gltf || !this.gltf.scene) {
            throw new Error("No GLB model loaded.");
        }

        const customData = this.gltf.scene.userData[this.USER_DATA_KEY] as
            | ICustomUserData
            | undefined;

        if (!customData) {
            throw new Error("No custom user data found on the model.");
        }

        // Convert Base64 data back into File objects
        const filePromises = customData.files.map((fileData) =>
            this.base64ToFile(fileData.data, fileData.name, fileData.type)
        );

        const files = await Promise.all(filePromises);

        return {
            data: customData.B,
            diamonds: files
        };
    }

    /**
     * Clears the currently loaded GLTF data.
     */
    public clear(): void {
        this.gltf = null;
    }

    /**
     * Helper to convert a File object to a Base64 Data URL.
     */
    private fileToBase64(file: File): Promise<IStoredFileData> {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () =>
                resolve({
                    name: file.name,
                    type: file.type,
                    data: reader.result as string
                });
            reader.onerror = (error) => reject(error);
        });
    }

    /**
     * Helper to convert a Base64 Data URL back to a File object.
     */
    private async base64ToFile(
        dataUrl: string,
        filename: string,
        mimeType: string
    ): Promise<File> {
        const res = await fetch(dataUrl);
        const blob = await res.blob();
        return new File([blob], filename, { type: mimeType });
    }
}
